import { useCallback, useEffect, useState } from 'react';
import { C } from '../lib/theme';
import { styles } from '../lib/styles';
import { SearchFilterBar, useSearchFilter } from './SearchFilterBar';
import { useAppData } from '../state/contexts/AppDataContext';
import { newIdempotencyKey } from '../lib/idempotency';
import { ApiProblem } from '../api/client';
import {
  listCustomerUsers,
  createCustomerUser,
  deleteCustomerUser,
  setCustomerUserEnabled,
  setCustomerUserTier,
  resendCustomerUserInvite,
  resetCustomerUserTotp,
  tierLabel,
  CUSTOMER_TIERS,
  TIER_LABELS,
  type AuthMode,
  type CustomerTier,
  type CustomerUser,
} from '../api/customerUsers';

// #296 — the Portal Logins tab of a company's detail on the Customers page: the
// people who sign in to the /apply/ submission portal, as opposed to that
// company's machine credential.
//
// #323 — always scoped to ONE company. Before the customers table existed this
// listed every login across every customer with a Customer facet, because there
// was no company record to hang them off. Now there is, so the company is the
// page and this is one of its tabs: the customerId is fixed by the caller and
// there is no way to create a login for a different company by accident.
//
// Every guardrail here is enforced server-side and mirrored only for affordance:
// a login can only be created for a company that exists in the customers table
// and is ACTIVE (422), staff accounts are unreachable from these routes (404),
// and the account is activated by an invite email rather than a password we
// hand over.

// An account that still owes UPDATE_PASSWORD / CONFIGURE_TOTP has never signed
// in. Surfacing that distinguishes "invite not accepted yet" from "broken",
// which is otherwise the most common support question about this page.
//
// What "Invited" does NOT mean (#351): it is derived from Keycloak's pending
// required actions, which record that we asked Keycloak to send an invite --
// never that the invite arrived. A mail that ACS suppressed, bounced or dropped
// leaves an account looking exactly like one whose owner has not opened it yet.
// That ambiguity cost hours of diagnosis on a real invite, so the caveat sits
// under the badge as always-visible text rather than letting the word imply
// delivery. Not a title= tooltip: #251 removed those from this SPA because
// hover-only text never reaches keyboard or touch users.
function activationLabel(u: CustomerUser): { text: string; color: string; note?: string } {
  if (!u.enabled) return { text: 'Disabled', color: C.danger };
  // #394 — an SSO login has no pending actions to clear, so it reads Active from
  // the moment it is created. That makes the badge mean something different
  // here, and the difference has to be said out loud: for a local login Active
  // is evidence the person completed the invite, while for an SSO login it is
  // only "provisioned and ready". Same reasoning as the #351 caveat below — a
  // badge that quietly implies more than it knows is what costs the diagnosis
  // hours.
  if (u.authMode === 'sso') {
    return {
      text: 'Active',
      color: C.success,
      note: 'Ready to sign in. Does not mean they have — an SSO login has no '
        + 'invite to accept, so it reads Active from the moment it is created.',
    };
  }
  if (u.pendingActions.length > 0) {
    return {
      text: 'Invited',
      color: C.warn,
      // #251: NOT a title= tooltip. Hover-only text never reaches keyboard or
      // touch users, and this caveat is the one that cost hours of diagnosis.
      note: 'Not proof the invite arrived. If a re-send also produces nothing, '
        + 'ask your operator to check delivery.',
    };
  }
  return { text: 'Active', color: C.success };
}

// #394 — how the person signs in. Read-only: there is no route that converts a
// login between modes, so this is a label rather than a control.
function authModeLabel(mode: AuthMode): { text: string; hint: string } {
  return mode === 'sso'
    ? { text: 'SSO', hint: 'Microsoft or Google account' }
    : { text: 'Password', hint: 'password + MFA held here' };
}

export function CustomerLogins({
  customerId,
  suspended = false,
  onChange,
}: {
  /** The company these logins belong to. Fixed — never chosen in this component. */
  customerId: string;
  /**
   * The company is SUSPENDED. The API 422s a create for it anyway; this hides
   * the form and says why, so the operator does not fill it in to be refused.
   */
  suspended?: boolean;
  /** Called after a create/enable/disable, so the parent can refresh its counts. */
  onChange?: () => void;
}) {
  const { showToast } = useAppData();

  const [users, setUsers] = useState<CustomerUser[] | null>(null);
  const [notConfigured, setNotConfigured] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [confirmId, setConfirmId] = useState<string | null>(null);
  // #463 — its own slot rather than sharing confirmId: deleting is
  // irreversible and must not be one mis-click away from the disable
  // confirmation that sits in the same cell.
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  // #394 — the address of an SSO login whose welcome mail could not be queued.
  // Not a boolean: the operator has to be told WHO to pass the URL on to.
  const [mailWarning, setMailWarning] = useState<string | null>(null);
  // #400 — which login's tier picker is open, and what it is set to. Kept as
  // one open row rather than a picker per row so the table has one editing
  // state, the same shape `confirmId` already uses for disable/enable.
  const [tierEditId, setTierEditId] = useState<string | null>(null);
  const [tierDraft, setTierDraft] = useState<CustomerTier>('viewer');
  // #394 — `local` is the default so the form behaves as it always has until an
  // operator chooses otherwise. #400 — `tier` deliberately has NO safe default:
  // the API refuses a create without one, and pre-filling the field here would
  // just move the unmade decision from the API to the form. `''` keeps the
  // submit button disabled until someone chooses.
  const [form, setForm] = useState<{
    email: string; firstName: string; lastName: string; authMode: AuthMode; tier: CustomerTier | '';
  }>({ email: '', firstName: '', lastName: '', authMode: 'local', tier: '' });

  const load = useCallback(() => {
    setLoadError(null);
    // Server-side filter, not a client-side one: the route takes customerId, so
    // a company's tab never downloads another company's logins.
    return listCustomerUsers(customerId)
      .then((items) => { setUsers(items); setNotConfigured(false); })
      .catch((err: unknown) => {
        if (err instanceof ApiProblem && err.status === 503) { setNotConfigured(true); setUsers([]); return; }
        setLoadError(err instanceof Error ? err.message : String(err));
      });
  }, [customerId]);

  useEffect(() => { load(); }, [load]);

  // #396 — the mail warning clears on a company switch, NOT inside load().
  // The Customers page renders this component WITHOUT a key, so switching
  // company reuses the same instance: without this, "Account created — but
  // nobody has been told" would sit above a different company's list, naming
  // an address that has nothing to do with it. But every row action
  // (disable/enable/invite/reset) also awaits load(), and the warning is an
  // errand reminder — pass on the URL — so it must survive those incidental
  // refreshes and go away only on dismiss, a successful new create, or a
  // company switch.
  useEffect(() => { setMailWarning(null); }, [customerId]);

  const userList = users ?? [];
  // #414 — the two things this tab owes an operator about the company as a
  // whole, both derived from the list it already has rather than from a new
  // request.
  //
  // Enabled-only on both counts: a disabled login cannot sign in, so a company
  // whose sole admin was offboarded is in exactly the state worth flagging, and
  // one whose every login is disabled is about to be re-populated from scratch.
  const enabledUsers = userList.filter((u) => u.enabled);
  // Nobody has ever signed in for this company. `users !== null` matters: while
  // the list is still loading it is not yet true that there are none, and
  // pre-selecting a capability on the strength of "we have not looked yet"
  // would be the silent grant #400 forbids.
  const isFirstLogin = users !== null && enabledUsers.length === 0;
  // A working admin is exactly one tier role that is `admin`. A login holding
  // admin AND something else is refused at the token guard as ambiguous, so it
  // is not an admin this company can use — counting it would hide the hole
  // behind a login that cannot sign in at all.
  const hasNoAdmin = enabledUsers.length > 0
    && !enabledUsers.some((u) => u.tiers.length === 1 && u.tiers[0] === 'admin');

  // #414 — the smart default, derived rather than written into `form` on load.
  // An explicit choice always wins, and because nothing is stored there is no
  // window in which the list request resolving could overwrite one. #400's rule
  // is intact: the API still requires an explicit tier and the human still
  // submits the choice — this fills in a field, it does not grant anything.
  const tierValue: CustomerTier | '' = form.tier !== ''
    ? form.tier
    : isFirstLogin ? 'admin' : '';
  const canCreate = form.email.trim().length > 0 && tierValue !== '';

  const reportError = useCallback((err: unknown) => {
    if (err instanceof ApiProblem) showToast(err.detail ? `${err.title}: ${err.detail}` : err.title);
    else showToast(err instanceof Error ? err.message : String(err));
  }, [showToast]);

  const act = useCallback(async (id: string, fn: () => Promise<void>, done: string) => {
    setBusyId(id);
    setConfirmId(null);
    // #463 — the delete slot is cleared here too. Leaving it armed after any
    // action meant the row re-rendered with "Confirm delete" still showing,
    // one click from destroying an account the operator had already moved on
    // from.
    setConfirmDeleteId(null);
    setTierEditId(null);
    try {
      await fn();
      await load();
      onChange?.();
      showToast(done, 'success');
    } catch (err) {
      reportError(err);
    } finally {
      setBusyId(null);
    }
  }, [load, onChange, reportError, showToast]);

  const submitCreate = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    // The submit button is disabled without a tier, so this cannot fire — but
    // the API's 400 is the real gate and the narrowing has to be honest.
    if (!tierValue) return;
    setCreating(true);
    try {
      const sso = form.authMode === 'sso';
      const email = form.email.trim();
      const created = await createCustomerUser({
        email,
        customerId,
        tier: tierValue,
        firstName: form.firstName.trim() || undefined,
        lastName: form.lastName.trim() || undefined,
        authMode: form.authMode,
      }, newIdempotencyKey());
      // The tier is NOT carried over to the next create, unlike authMode: a
      // company's logins are usually all on the same sign-in method but not all
      // at the same access level, and the one to re-decide every time is the
      // one that grants capability. Clearing it back to '' also retires the
      // #414 first-login default in one move: the reload below finds an enabled
      // login, so the second invite starts on "Choose…" like every other.
      setForm({ email: '', firstName: '', lastName: '', authMode: form.authMode, tier: '' });
      await load();
      onChange?.();

      // #394 — an SSO create whose welcome mail could not be queued still
      // succeeds, because the account works without it. That makes it the one
      // outcome on this form that a success toast would actively misreport: the
      // login is live and nobody has been told. It gets a banner that stays
      // rather than a toast that scrolls away, because it leaves the operator
      // with an errand — pass on the URL — and losing the message loses the
      // errand.
      if (sso && created.welcomeEmailQueued === false) {
        setMailWarning(email);
        showToast(
          `Login created, but the welcome email to ${email} could not be queued. `
            + 'Tell them to sign in at /apply/ — the account itself is ready to use.',
        );
        return;
      }
      setMailWarning(null);
      showToast(
        sso
          ? 'SSO login created and ready to use. A welcome email has been sent — they sign in with their '
            + 'Microsoft or Google account, and there is no password or MFA for us to set.'
          : 'Login created. An invite email has been sent — the person sets their own password and enrolls MFA.',
        'success',
      );
    } catch (err) {
      reportError(err);
    } finally {
      setCreating(false);
    }
  }, [customerId, form, load, onChange, reportError, showToast, tierValue]);

  // No Customer facet: every row here belongs to the one company whose tab this
  // is, so a facet with a single option would only be noise.
  const { filtered, bar } = useSearchFilter(
    userList,
    (u) => [u.username, u.email, u.name].filter(Boolean).join(' '),
    [
      {
        id: 'state',
        label: 'State',
        options: [
          { value: '', label: 'All states' },
          { value: 'active', label: 'Active only' },
          { value: 'invited', label: 'Invited (not yet signed in)' },
          { value: 'disabled', label: 'Disabled only' },
        ],
        predicate: (u, v) => (
          v === 'disabled' ? !u.enabled
            : v === 'invited' ? u.enabled && u.pendingActions.length > 0
              : u.enabled && u.pendingActions.length === 0
        ),
      },
      // #394 — the review question this answers is "who is still on a password
      // we hold?", which is the set that needs invite and MFA-reset support and
      // that does not go away when the person leaves their employer.
      {
        id: 'mode',
        label: 'Sign-in',
        options: [
          { value: '', label: 'Any sign-in method' },
          { value: 'sso', label: 'SSO (Microsoft / Google)' },
          { value: 'local', label: 'Password held here' },
        ],
        predicate: (u, v) => u.authMode === v,
      },
      // #400 — "who can submit on this company's behalf?" is the access review
      // this tab exists to answer, and `needs-attention` is the one that has to
      // be findable: it collects both off-contract states (no tier, or more
      // than one), which are the logins that cannot sign in at all.
      {
        id: 'tier',
        // Short, like its neighbours "State" and "Sign-in", and deliberately
        // NOT "Access level" — that is the invite form's field. Two controls
        // sharing an accessible name is a real ambiguity for a screen reader,
        // and the existing Sign-in / Sign-in method pair set the precedent.
        label: 'Access',
        options: [
          { value: '', label: 'Any access level' },
          ...CUSTOMER_TIERS.map((t) => ({ value: t, label: TIER_LABELS[t].text })),
          { value: 'needs-attention', label: 'Needs attention (no / conflicting level)' },
        ],
        predicate: (u, v) => (
          v === 'needs-attention' ? u.tiers.length !== 1 : u.tiers.length === 1 && u.tiers[0] === v
        ),
      },
    ],
    { placeholder: 'Search logins by email or name…' },
  );

  return (
    <div>
      <div style={{ color: C.textMuted, fontSize: 13, marginBottom: 20 }}>
        Named logins for the customer submission portal, bound to <code>{customerId}</code>. Each sees exactly
        what this company&apos;s API integration sees, so submissions are attributable to a person in the audit
        trail. Choose how each person signs in: <strong>SSO</strong> with their own Microsoft or Google account,
        or a <strong>password</strong> they set from an invite email and an authenticator they enroll. No password
        is ever set here in either case. Every login also carries an <strong>access level</strong> — Viewer,
        Submitter or Admin — which decides whether they can only read this company&apos;s submissions or can also
        create, edit and withdraw them. Disable before you delete: a disabled login keeps its audit history and can be restored, and deleting is only offered once a login is already disabled.
      </div>

      {notConfigured && (
        <div style={{ ...styles.card, borderLeft: `3px solid ${C.warn}`, color: C.warn }} role="alert">
          <strong>Customer API management is not configured.</strong> The Keycloak admin service account
          (<code>bcid-admin-svc</code>) has not been provisioned on this deployment yet.
        </div>
      )}

      {loadError && (
        <div style={{ ...styles.card, color: C.danger }} role="alert">Failed to load portal logins: {loadError}</div>
      )}

      {/* #394 — the SSO create succeeded and the welcome mail did not. Says who,
          says what to do, and stays put until dismissed: the account is already
          usable, so the only thing outstanding is telling the person, and a
          toast that scrolls away is exactly how that gets forgotten. */}
      {mailWarning && (
        <div
          style={{ ...styles.card, borderLeft: `3px solid ${C.warn}`, color: C.warn, marginBottom: 16 }}
          role="alert"
        >
          <strong>Account created — but nobody has been told.</strong> The welcome email to{' '}
          <code>{mailWarning}</code> could not be queued, so it was not sent and there is nothing to re-send.
          The login itself is ready: tell them to go to <code>/apply/</code> and sign in with the Microsoft or
          Google account for that address. Ask your operator to check the mail path
          (<code>pm2 logs bcid-api</code>) before creating more SSO logins.
          <div>
            <button
              style={{ ...styles.btn, ...styles.btnOutline, ...styles.btnSmall, marginTop: 10 }}
              onClick={() => setMailWarning(null)}
            >Dismiss</button>
          </div>
        </div>
      )}

      {!notConfigured && !loadError && (
        <>
          {suspended ? (
            <div style={{ ...styles.card, borderLeft: `3px solid ${C.warn}`, color: C.warn, marginBottom: 16 }} role="alert">
              <strong>This company is suspended.</strong> New logins cannot be invited while it is — the API
              refuses them. Reactivate the company on the Overview tab first, then enable the individual logins
              you want back.
            </div>
          ) : (
            <form style={{ ...styles.card, marginBottom: 16 }} onSubmit={submitCreate}>
              <div style={{ fontWeight: 600, color: C.text, marginBottom: 12 }}>Invite a portal login</div>
              <div style={{ display: 'grid', gridTemplateColumns: '2fr 1.2fr 1.2fr 1fr 1fr auto', gap: 10, alignItems: 'end' }}>
                <label style={{ fontSize: 12, color: C.textMuted }}>
                  Work email
                  <input
                    style={{ ...styles.input, marginTop: 4 }}
                    type="email"
                    required
                    value={form.email}
                    onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                    placeholder="person@customer.example"
                  />
                </label>
                {/* #394 — chosen per login and not changeable afterwards, so it
                    sits next to the address rather than behind an "advanced"
                    disclosure. In SSO mode the address is also the realm's
                    auto-link key, which is what the hint below is warning
                    about. */}
                <label style={{ fontSize: 12, color: C.textMuted }}>
                  Sign-in method
                  <select
                    // Explicit, because the accessible name a wrapping <label>
                    // gives a <select> otherwise picks up the option text too.
                    aria-label="Sign-in method"
                    style={{ ...styles.input, marginTop: 4 }}
                    value={form.authMode}
                    onChange={(e) => setForm((f) => ({ ...f, authMode: e.target.value as AuthMode }))}
                  >
                    <option value="local">Password + MFA (we hold it)</option>
                    <option value="sso">SSO — Microsoft or Google</option>
                  </select>
                </label>
                {/* #400 — no pre-selected option. The placeholder is not a
                    value: leaving it selected keeps the button disabled, so
                    creating a login is always a deliberate answer to "what may
                    this person do?" rather than an accepted default.
                    #414 — with one exception, the company's FIRST login, where
                    the placeholder is dropped and Admin is pre-selected. That
                    is not a silent grant: the field is filled in, visibly,
                    beside a hint saying why, and the operator still submits it.
                    The placeholder goes away in that mode on purpose — leaving
                    it would offer "Choose…" as a way to un-answer a question
                    that now has an answer on screen. */}
                <label style={{ fontSize: 12, color: C.textMuted }}>
                  Access level
                  <select
                    aria-label="Access level"
                    aria-describedby={isFirstLogin ? 'first-login-tier-hint' : undefined}
                    style={{ ...styles.input, marginTop: 4 }}
                    required
                    value={tierValue}
                    onChange={(e) => setForm((f) => ({ ...f, tier: e.target.value as CustomerTier | '' }))}
                  >
                    {!isFirstLogin && <option value="">Choose…</option>}
                    {CUSTOMER_TIERS.map((t) => (
                      <option key={t} value={t}>{TIER_LABELS[t].text} — {TIER_LABELS[t].hint}</option>
                    ))}
                  </select>
                </label>
                <label style={{ fontSize: 12, color: C.textMuted }}>
                  First name
                  <input
                    style={{ ...styles.input, marginTop: 4 }}
                    value={form.firstName}
                    onChange={(e) => setForm((f) => ({ ...f, firstName: e.target.value }))}
                  />
                </label>
                <label style={{ fontSize: 12, color: C.textMuted }}>
                  Last name
                  <input
                    style={{ ...styles.input, marginTop: 4 }}
                    value={form.lastName}
                    onChange={(e) => setForm((f) => ({ ...f, lastName: e.target.value }))}
                  />
                </label>
                <button style={{ ...styles.btn, ...styles.btnGold }} type="submit" disabled={!canCreate || creating}>
                  {creating ? 'Sending…' : form.authMode === 'sso' ? 'Create SSO login' : 'Send invite'}
                </button>
              </div>
              {/* The address is load-bearing in SSO mode in a way it is not in
                  local mode: the realm auto-links a federated sign-in by
                  verified email, so the address IS the tenant binding, and a
                  typo that happens to be a real mailbox hands this company's
                  submissions to whoever owns it. */}
              <div style={{ fontSize: 11, color: C.textMuted, marginTop: 10, maxWidth: 640 }}>
                {form.authMode === 'sso'
                  ? 'They sign in with the Microsoft or Google account for this exact address — check it '
                    + 'carefully, it is what binds them to this company. No password or MFA is set here, and '
                    + 'there is no invite to accept: the login works as soon as you create it.'
                  : 'They will get an invite email valid for 72 hours, set their own password, and enroll an '
                    + 'authenticator. 382 never knows their password.'}
              </div>
              {/* #414 — says WHY the level was filled in, and that it is still
                  the operator's to change. Referenced by aria-describedby from
                  the picker rather than left as loose copy: a pre-filled field
                  whose reason a screen reader never reaches is exactly the
                  default that gets accepted without being read. */}
              {isFirstLogin && (
                <div
                  id="first-login-tier-hint"
                  style={{ fontSize: 11, color: C.text, marginTop: 10, maxWidth: 640 }}
                >
                  <strong>First login for this company.</strong> Pre-set to{' '}
                  <strong>Admin</strong> — the level that lets a company manage its own portal logins
                  (#403) rather than asking 382 for every add, disable and access change. Nobody at
                  this company can grant it, so if this login is not an admin, the company only gets
                  one if you come back and make one. Override it if this person should not be that.
                </div>
              )}
              {/* No customer picker: the company is the page. #323 removed the
                  old "create an intake client first" warning that used to live
                  here — an intake client is no longer a prerequisite for a
                  login, the customer record is, and it demonstrably exists. */}
            </form>
          )}

          {/* #414 — the same hole as the first-login default, arrived at by
              drift instead: the only admin was disabled, the tenant predates
              #400, or every login was deliberately made a submitter. It sits
              above the list because the usual fix is Change on one of those
              rows, and it stays as long as the condition does — there is
              nothing to dismiss, because dismissing would not fix it.

              Deliberately NOT role="alert". This is a standing property of the
              company, present from first render, not something that just
              happened; the one live region on this tab is the #394 mail warning,
              which reports an event and would lose its urgency in a crowd. */}
          {hasNoAdmin && (
            <div style={{ ...styles.card, borderLeft: `3px solid ${C.warn}`, color: C.warn, marginBottom: 16 }}>
              <strong>No admin for this company.</strong> None of its enabled logins holds a working
              Admin access level — one with no level, or with two, does not count, because it cannot
              sign in at all. Admin is what lets a company manage its own portal logins (#403)
              instead of asking 382 for every add, disable and access change. Use <strong>Change</strong>{' '}
              on the row of whoever should hold it.
            </div>
          )}

          {users && (
            <div style={styles.card}>
              <SearchFilterBar {...bar} />
              <table style={styles.table}>
                <thead><tr>
                  <th style={styles.th}>Email</th>
                  <th style={styles.th}>Name</th>
                  <th style={styles.th}>Sign-in</th>
                  <th style={styles.th}>Access level</th>
                  <th style={styles.th}>State</th>
                  <th style={styles.th}>Actions</th>
                </tr></thead>
                <tbody>
                  {filtered.map((u) => {
                    const busy = busyId === u.keycloakId;
                    const confirming = confirmId === u.keycloakId;
                    const state = activationLabel(u);
                    const mode = authModeLabel(u.authMode);
                    const tier = tierLabel(u.tiers);
                    const editingTier = tierEditId === u.keycloakId;
                    // #394 — both of these WRITE requiredActions, so on an SSO
                    // login they are not no-ops: they would strand a working
                    // federated account behind a password/TOTP wizard it cannot
                    // satisfy. The API refuses them with a 409; not offering
                    // them is what stops an admin meeting that refusal.
                    const localOnlyActions = u.authMode === 'local';
                    return (
                      <tr key={u.keycloakId}>
                        <td style={styles.td}><code style={{ fontSize: 13 }}>{u.email ?? u.username}</code></td>
                        <td style={styles.td}>{u.name}</td>
                        <td style={styles.td}>
                          <span style={{ ...styles.badge, color: u.authMode === 'sso' ? C.info : C.textMuted }}>
                            {mode.text}
                          </span>
                          <div style={{ fontSize: 11, color: C.textMuted, marginTop: 2 }}>{mode.hint}</div>
                        </td>
                        {/* #400 — the badge doubles as the control, because the
                            two states worth acting on (no level, conflicting)
                            are exactly the ones an operator must be able to
                            repair from where they see them. The picker writes
                            on change rather than behind a Save: the API call is
                            idempotent, and a confirm step here would be
                            ceremony on a reversible change. */}
                        <td style={styles.td}>
                          {editingTier ? (
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
                              <select
                                aria-label={`Access level for ${u.email ?? u.username}`}
                                style={{ ...styles.input, width: 'auto' }}
                                disabled={busy}
                                value={tierDraft}
                                onChange={(e) => setTierDraft(e.target.value as CustomerTier)}
                              >
                                {CUSTOMER_TIERS.map((t) => (
                                  <option key={t} value={t}>{TIER_LABELS[t].text}</option>
                                ))}
                              </select>
                              <button
                                style={{ ...styles.btn, ...styles.btnGold, ...styles.btnSmall }}
                                disabled={busy}
                                onClick={() => act(
                                  u.keycloakId,
                                  async () => { await setCustomerUserTier(u.keycloakId, tierDraft); },
                                  `Access level set to ${TIER_LABELS[tierDraft].text}.`,
                                )}
                              >Save</button>
                              <button
                                style={{ ...styles.btn, ...styles.btnOutline, ...styles.btnSmall }}
                                disabled={busy}
                                onClick={() => setTierEditId(null)}
                              >Cancel</button>
                            </div>
                          ) : (
                            <>
                              <span style={{ ...styles.badge, color: tier.ok ? C.textMuted : C.warn }}>
                                {tier.text}
                              </span>
                              <div style={{ fontSize: 11, color: C.textMuted, marginTop: 2, maxWidth: 220 }}>
                                {tier.hint}
                              </div>
                              <button
                                style={{ ...styles.btn, ...styles.btnOutline, ...styles.btnSmall, marginTop: 6 }}
                                disabled={busy}
                                onClick={() => {
                                  setTierDraft(u.tiers.length === 1 ? u.tiers[0] : 'viewer');
                                  setTierEditId(u.keycloakId);
                                }}
                              >Change</button>
                            </>
                          )}
                        </td>
                        <td style={styles.td}>
                          <span style={{ ...styles.badge, color: state.color }}>{state.text}</span>
                          {state.note && (
                            <div style={{ fontSize: 11, color: C.textMuted, marginTop: 2, maxWidth: 260 }}>
                              {state.note}
                            </div>
                          )}
                        </td>
                        <td style={styles.td}>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                            {localOnlyActions && (
                              <>
                                <button
                                  style={{ ...styles.btn, ...styles.btnOutline, ...styles.btnSmall }}
                                  disabled={busy}
                                  onClick={() => act(u.keycloakId, () => resendCustomerUserInvite(u.keycloakId), 'Invite email re-sent.')}
                                >Re-send invite</button>
                                <button
                                  style={{ ...styles.btn, ...styles.btnOutline, ...styles.btnSmall }}
                                  disabled={busy}
                                  onClick={() => act(u.keycloakId, () => resetCustomerUserTotp(u.keycloakId), 'MFA cleared. They will re-enroll at next sign-in.')}
                                >Reset MFA</button>
                              </>
                            )}
                            {/* #463 — offered only on an already-disabled
                                login, matching the API, and with its own
                                confirm slot so it can never be reached by a
                                mis-click from the disable confirmation. */}
                            {!u.enabled && (
                              confirmDeleteId === u.keycloakId ? (
                                <>
                                  <button
                                    style={{ ...styles.btn, ...styles.btnSmall, ...styles.btnDanger }}
                                    disabled={busy}
                                    onClick={() => {
                                      setConfirmDeleteId(null);
                                      void act(
                                        u.keycloakId,
                                        () => deleteCustomerUser(u.keycloakId),
                                        'Login deleted. Their submissions and audit history are unchanged.',
                                      );
                                    }}
                                  >Confirm delete</button>
                                  <button
                                    style={{ ...styles.btn, ...styles.btnOutline, ...styles.btnSmall }}
                                    disabled={busy}
                                    onClick={() => setConfirmDeleteId(null)}
                                  >Cancel</button>
                                </>
                              ) : (
                                <button
                                  style={{ ...styles.btn, ...styles.btnOutline, ...styles.btnSmall }}
                                  disabled={busy}
                                  onClick={() => { setConfirmId(null); setConfirmDeleteId(u.keycloakId); }}
                                >Delete</button>
                              )
                            )}
                            {confirming ? (
                              <>
                                <button
                                  style={{ ...styles.btn, ...styles.btnSmall, ...styles.btnDanger }}
                                  disabled={busy}
                                  onClick={() => act(
                                    u.keycloakId,
                                    () => setCustomerUserEnabled(u.keycloakId, !u.enabled),
                                    u.enabled
                                      ? 'Login disabled. They can no longer sign in; their submissions and audit history are retained.'
                                      : 'Login enabled.',
                                  )}
                                >{u.enabled ? 'Confirm disable' : 'Confirm enable'}</button>
                                <button
                                  style={{ ...styles.btn, ...styles.btnOutline, ...styles.btnSmall }}
                                  disabled={busy}
                                  onClick={() => setConfirmId(null)}
                                >Cancel</button>
                              </>
                            ) : (
                              <button
                                style={{ ...styles.btn, ...styles.btnOutline, ...styles.btnSmall }}
                                disabled={busy}
                                onClick={() => { setConfirmDeleteId(null); setConfirmId(u.keycloakId); }}
                              >{u.enabled ? 'Disable' : 'Enable'}</button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                  {filtered.length === 0 && (
                    <tr><td style={styles.td} colSpan={6}>
                      <span style={{ color: C.textMuted }}>
                        {userList.length === 0
                          ? 'No portal logins for this company yet.'
                          : 'No logins match the current filters.'}
                      </span>
                    </td></tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}
